import type { QuoteRecipientRow } from 'Cube/view/layouts/quote/components/QuoteRecipientsNotifyList'

/**
 * How far a recipient has got with the quote we emailed them.
 *
 *   NOT_NOTIFIED -> NOTIFIED -> SIGNED
 *
 * Someone copied in never signs, so they stop at NOTIFIED.
 */
export type NotificationStatus = 'NOT_NOTIFIED' | 'NOTIFIED' | 'SIGNED'

export type QuoteRecipient = {
  name: string
  email: string
  notificationStatus: NotificationStatus
  signedAt: string | undefined
  lastNotifiedAt: string | undefined
}

export type QuoteRecipientGroups = {
  signers: QuoteRecipient[]
  countersigners: QuoteRecipient[]
  copied: QuoteRecipient[]
}

export const getNotificationStatus = ({
  signedAt,
  lastNotifiedAt
}: {
  signedAt: string | undefined
  lastNotifiedAt: string | undefined
}): NotificationStatus => {
  if (signedAt) return 'SIGNED'
  if (lastNotifiedAt) return 'NOTIFIED'
  return 'NOT_NOTIFIED'
}

/**
 * Splits the quote's recipients into the three groups the sidebar lists: the
 * people being asked to sign, the people signing it back on our side, and the
 * people only copied in.
 *
 * A row is identified by its email here, rather than by the contact or
 * countersigner id the rows carry, because one person can hold more than one of
 * those roles at once. They get a single row, in the first group that claims
 * them - the same rule the quote applies when it saves the lists.
 */
export const toRecipientGroups = (
  rows: QuoteRecipientRow[]
): QuoteRecipientGroups => {
  const signers = dedupeByEmail(rows.filter(row => row.kind === 'SIGNER'))
  const countersigners = notAlreadyListed(
    dedupeByEmail(rows.filter(row => row.kind === 'COUNTER_SIGNER')),
    signers
  )
  const copied = notAlreadyListed(
    dedupeByEmail(rows.filter(row => row.kind === 'CC')),
    [...signers, ...countersigners]
  )

  return {
    signers: signers.map(toRecipient),
    countersigners: countersigners.map(toRecipient),
    copied: copied.map(toRecipient)
  }
}

const notAlreadyListed = (
  rows: QuoteRecipientRow[],
  listed: QuoteRecipientRow[]
): QuoteRecipientRow[] => {
  const listedEmails = new Set(listed.map(row => row.email))

  return rows.filter(row => !listedEmails.has(row.email))
}

const toRecipient = (row: QuoteRecipientRow): QuoteRecipient => ({
  name: row.name,
  email: row.email,
  signedAt: row.signedAt,
  lastNotifiedAt: row.lastNotifiedAt,
  notificationStatus: getNotificationStatus({
    signedAt: row.signedAt,
    lastNotifiedAt: row.lastNotifiedAt
  })
})

const dedupeByEmail = (rows: QuoteRecipientRow[]): QuoteRecipientRow[] =>
  rows.reduce<QuoteRecipientRow[]>(
    (acc, row) =>
      acc.some(existing => existing.email === row.email) ? acc : [...acc, row],
    []
  )
